/**
 * Escape belongs to the innermost thing on screen. A panel that closes on
 * Escape has to know when a menu, select, dialog or popover is layered over it
 * so the keypress dismisses that layer and leaves the panel alone.
 *
 * Radix dismisses its topmost layer from a *capture-phase* `keydown` listener
 * on the document, which runs before every bubble-phase handler in the app —
 * so a panel-level handler cannot tell "nothing was open" from "the menu just
 * took this keypress" without looking. The DOM is what it looks at: the layer
 * is still marked open at that point, because Radix's `setState` has not
 * re-rendered yet.
 */

/**
 * Layers that own Escape when the keyboard is inside them. Anything holding
 * focus and calling itself a menu, list, dialog or popover handles its own
 * dismiss, hand-rolled or not.
 */
const FOCUSED_ESCAPE_LAYER_SELECTOR = [
  '[role="dialog"]:not([data-state="closed"])',
  '[role="alertdialog"]:not([data-state="closed"])',
  '[role="menu"]:not([data-state="closed"])',
  '[role="listbox"]:not([data-state="closed"])',
  '[data-radix-menu-content]:not([data-state="closed"])',
  '[data-radix-select-content]:not([data-state="closed"])',
  '[data-radix-popover-content]:not([data-state="closed"])',
].join(", ");

/**
 * Layers that own Escape even with focus elsewhere — non-modal menus and
 * dialogs that leave focus on the body.
 *
 * `data-state="open"` is the qualifier, and it is doing real work: it marks a
 * floating layer that is up right now, which a permanently mounted panel that
 * merely calls itself a dialog (the canvas onboarding card, for one) never is,
 * and which a dismissed layer stops being while it animates out — that next
 * Escape belongs to the container, not to a menu already leaving.
 */
export const OPEN_ESCAPE_LAYER_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[data-radix-menu-content][data-state="open"]',
  '[data-radix-select-content][data-state="open"]',
  '[data-radix-popover-content][data-state="open"]',
].join(", ");

/**
 * Whether an open overlay should consume this Escape instead of the caller.
 *
 * Deliberately not keyed off `defaultPrevented`: Radix calls `preventDefault()`
 * for its topmost layer even when that layer is already dismissed and only
 * animating out, which would swallow the Escape meant for the container.
 */
export function isEscapeOwnedByOverlay(
  event: Pick<KeyboardEvent, "target">,
  root: ParentNode = document,
): boolean {
  const target = event.target as HTMLElement | null;
  if (target?.closest?.(FOCUSED_ESCAPE_LAYER_SELECTOR)) return true;
  return Boolean(root.querySelector?.(OPEN_ESCAPE_LAYER_SELECTOR));
}
